import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import {
  Clock,
  Calendar,
  User,
  Tag,
  ArrowLeft,
  ArrowRight,
  Share2,
  Bookmark,
  Facebook,
  Twitter,
  Linkedin,
  Eye,
  ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchArticle = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get(`${API}/blog/articles/${slug}`);
      setArticle(response.data);
    } catch (err) {
      console.error("Error fetching article:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const shareArticle = (platform) => {
    const url = window.location.href;
    const title = article?.title || "";
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard");
  };

  const getCategoryInfo = (categorySlug) => {
    const categories = {
      rera: { name: "RERA & Compliance", color: "bg-emerald-100 text-emerald-700" },
      buying_guide: { name: "Buying Guide", color: "bg-blue-100 text-blue-700" },
      finance: { name: "Home Finance", color: "bg-amber-100 text-amber-700" },
      market_trends: { name: "Market Trends", color: "bg-purple-100 text-purple-700" },
      legal: { name: "Legal & Documentation", color: "bg-red-100 text-red-700" }
    };
    return categories[categorySlug] || { name: categorySlug, color: "bg-stone-100 text-stone-700" };
  };

  const categoryInfo = article ? getCategoryInfo(article.category) : { name: "", color: "" };
  
  const structuredData = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description,
    "image": article.featured_image,
    "author": {
      "@type": "Person",
      "name": article.author,
      "jobTitle": article.author_title
    },
    "publisher": {
      "@type": "Organization",
      "name": "RERA Approved",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  } : null;

  // SEO - Set document title and meta tags (must be before conditional returns)
  useEffect(() => {
    if (article) {
      document.title = article.meta_title || article.title;
      
      const setMeta = (name, content, property = false) => {
        if (!content) return;
        const attr = property ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attr}="${name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attr, name);
          document.head.appendChild(meta);
        }
        meta.content = content;
      };
      
      setMeta('description', article.meta_description);
      setMeta('keywords', article.meta_keywords?.join(", ") || "");
      setMeta('og:title', article.meta_title || article.title, true);
      setMeta('og:description', article.meta_description, true);
      setMeta('og:image', article.featured_image, true);
      setMeta('og:url', window.location.href, true);
      setMeta('og:type', 'article', true);
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', article.meta_title || article.title);
      setMeta('twitter:description', article.meta_description);
      setMeta('twitter:image', article.featured_image);

      // Add structured data
      if (structuredData) {
        let script = document.querySelector('script[type="application/ld+json"]');
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(structuredData);
      }
    }
  }, [article, structuredData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 py-12">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-stone-200 rounded w-3/4 mb-4" />
              <div className="h-4 bg-stone-200 rounded w-1/2 mb-8" />
              <div className="aspect-[21/9] bg-stone-200 rounded mb-8" />
              <div className="space-y-4">
                <div className="h-4 bg-stone-200 rounded w-full" />
                <div className="h-4 bg-stone-200 rounded w-full" />
                <div className="h-4 bg-stone-200 rounded w-3/4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-stone-50 py-12">
        <div className="section-container">
          <div className="max-w-2xl mx-auto text-center py-20">
            <h1 className="font-display text-3xl text-stone-800 mb-4">Article Not Found</h1>
            <p className="text-stone-600 mb-8">The article you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog" className="inline-flex items-center gap-2 bg-emerald-700 text-white px-6 py-3 hover:bg-emerald-800 transition-colors">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <article className="min-h-screen bg-stone-50" data-testid="blog-article-page">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="section-container py-4">
            <nav className="flex items-center gap-2 text-sm text-stone-500">
              <Link to="/" className="hover:text-emerald-700">Home</Link>
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              <Link to="/blog" className="hover:text-emerald-700">Blog</Link>
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              <Link to={`/blog?category=${article.category}`} className="hover:text-emerald-700">
                {categoryInfo.name}
              </Link>
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-stone-800 truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Header */}
        <header className="bg-white">
          <div className="section-container py-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <Link
                  to={`/blog?category=${article.category}`}
                  className={`px-3 py-1 text-xs uppercase tracking-widest font-medium rounded-full ${categoryInfo.color}`}
                >
                  {categoryInfo.name}
                </Link>
                <span className="text-stone-400">•</span>
                <span className="text-sm text-stone-500 flex items-center gap-1">
                  <Clock className="w-4 h-4" strokeWidth={1.5} />
                  {article.read_time} min read
                </span>
                <span className="text-stone-400">•</span>
                <span className="text-sm text-stone-500 flex items-center gap-1">
                  <Eye className="w-4 h-4" strokeWidth={1.5} />
                  {article.views?.toLocaleString()} views
                </span>
              </div>
              
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-800 mb-6 leading-tight">
                {article.title}
              </h1>
              
              <p className="text-xl text-stone-600 mb-8">
                {article.excerpt}
              </p>
              
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-emerald-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-medium text-stone-800">{article.author}</p>
                    <p className="text-sm text-stone-500">{article.author_title}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-stone-500 mr-2">Share:</span>
                  <button
                    onClick={() => shareArticle('facebook')}
                    className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => shareArticle('twitter')}
                    className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => shareArticle('linkedin')}
                    className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={copyLink}
                    className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                    aria-label="Copy link"
                  >
                    <Share2 className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="section-container pb-12">
          <div className="max-w-5xl mx-auto">
            <img
              src={article.featured_image}
              alt={article.title}
              className="w-full aspect-[21/9] object-cover rounded-sm shadow-lg"
            />
          </div>
        </div>

        {/* Article Content */}
        <div className="section-container pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-8">
                <div 
                  className="prose prose-lg prose-stone max-w-none
                    prose-headings:font-display prose-headings:text-stone-800
                    prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                    prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                    prose-p:text-stone-600 prose-p:leading-relaxed
                    prose-ul:text-stone-600 prose-ol:text-stone-600
                    prose-li:my-1
                    prose-strong:text-stone-800
                    prose-a:text-emerald-700 prose-a:no-underline hover:prose-a:underline
                    prose-table:text-sm prose-th:bg-stone-100 prose-th:p-3 prose-td:p-3 prose-td:border"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="mt-12 pt-8 border-t">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-stone-500 text-sm">Tags:</span>
                    {article.tags?.map((tag) => (
                      <Link
                        key={tag}
                        to={`/blog?tag=${encodeURIComponent(tag)}`}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-stone-100 text-stone-600 text-sm rounded-full hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                      >
                        <Tag className="w-3 h-3" strokeWidth={1.5} />
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Author Box */}
                <div className="mt-8 p-6 bg-stone-100 rounded-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-8 h-8 text-emerald-700" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800 text-lg">{article.author}</p>
                      <p className="text-emerald-700 text-sm mb-2">{article.author_title}</p>
                      <p className="text-stone-600 text-sm">
                        Expert contributor at RERA Approved, sharing insights on real estate, property investment, and homebuying in India.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-4">
                <div className="sticky top-24">
                  {/* CTA */}
                  <div className="bg-emerald-900 p-6 rounded-sm text-white mb-6">
                    <h3 className="font-display text-lg mb-2">Ready to Find Your Home?</h3>
                    <p className="text-emerald-100/80 text-sm mb-4">
                      Browse RERA-verified properties across India's top metro cities.
                    </p>
                    <Link
                      to="/search"
                      className="inline-flex items-center gap-2 bg-white text-emerald-900 px-4 py-2 text-sm font-medium w-full justify-center hover:bg-stone-50 transition-colors"
                    >
                      Search Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                  </div>

                  {/* EMI Calculator CTA */}
                  <div className="bg-amber-50 border border-amber-200 p-6 rounded-sm">
                    <h3 className="font-display text-lg text-stone-800 mb-2">Calculate Your EMI</h3>
                    <p className="text-stone-600 text-sm mb-4">
                      Plan your home loan with our easy EMI calculator.
                    </p>
                    <Link
                      to="/emi-calculator"
                      className="inline-flex items-center gap-2 text-amber-700 text-sm font-medium hover:gap-3 transition-all"
                    >
                      Try EMI Calculator <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {article.related_articles?.length > 0 && (
          <section className="bg-white py-16">
            <div className="section-container">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-display text-2xl text-stone-800 mb-8">Related Articles</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {article.related_articles.map((related) => (
                    <Link
                      key={related.article_id}
                      to={`/blog/${related.slug}`}
                      className="group"
                    >
                      <div className="aspect-[16/9] overflow-hidden rounded-sm mb-4">
                        <img
                          src={related.featured_image}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="font-display text-lg text-stone-800 group-hover:text-emerald-700 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <p className="text-sm text-stone-500 mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" strokeWidth={1.5} />
                        {related.read_time} min read
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Back to Blog */}
        <div className="bg-stone-100 py-8">
          <div className="section-container">
            <div className="flex justify-center">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-emerald-700 font-medium hover:gap-3 transition-all"
              >
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                Back to all articles
              </Link>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogArticlePage;
